// src/components/tactic/tacticUi.jsx
// Small shared shell pieces for the tactic board, so the page files stay
// about behaviour instead of padding and borders.

import React from "react";

export const T = {
  bg: "#07080b",
  panel: "rgba(255,255,255,0.028)",
  panelSolid: "#0d0f14",
  line: "rgba(255,255,255,0.09)",
  ink: "#ececee",
  dim: "#8b8e98",
  faint: "#5b5e67",
  accent: "#ff1026",
  blue: "#3b9dff",
  red: "#ff4d6d",
  green: "#34d399",
  font: "'Kanit', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  mono: "ui-monospace, 'SF Mono', Menlo, monospace",
};

/** Subscribe to a CSS media query, so layout can react without a CSS file. */
export function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );

  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

export function Panel({ title, right, children, style, bodyStyle }) {
  return (
    <section
      style={{
        background: T.panel,
        border: `1px solid ${T.line}`,
        borderRadius: 12,
        overflow: "hidden",
        ...style,
      }}
    >
      {title && (
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "9px 12px",
            borderBottom: `1px solid ${T.line}`,
            fontSize: 12,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: T.dim,
            fontWeight: 600,
          }}
        >
          <span>{title}</span>
          {right}
        </header>
      )}
      <div style={{ padding: 12, ...bodyStyle }}>{children}</div>
    </section>
  );
}

export function Btn({ children, active, tone = "default", disabled, style, ...rest }) {
  const tones = {
    default: { bg: "rgba(255,255,255,0.05)", fg: T.ink, bd: T.line },
    accent: { bg: "rgba(255,16,38,0.16)", fg: "#ffb1bb", bd: "rgba(255,16,38,0.5)" },
    blue: { bg: "rgba(59,157,255,0.15)", fg: "#9ccbff", bd: "rgba(59,157,255,0.45)" },
    red: { bg: "rgba(255,77,109,0.15)", fg: "#ffa3b4", bd: "rgba(255,77,109,0.45)" },
    danger: { bg: "rgba(255,77,109,0.10)", fg: "#ff8ba3", bd: "rgba(255,77,109,0.35)" },
    ghost: { bg: "transparent", fg: T.dim, bd: "transparent" },
  };
  const c = tones[tone] || tones.default;
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        appearance: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: T.font,
        fontSize: 13,
        fontWeight: 500,
        padding: "7px 11px",
        borderRadius: 8,
        color: active ? "#fff" : c.fg,
        background: active ? "rgba(255,16,38,0.28)" : c.bg,
        border: `1px solid ${active ? T.accent : c.bd}`,
        opacity: disabled ? 0.45 : 1,
        transition: "background .15s, border-color .15s",
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Row({ children, gap = 8, wrap = true, style }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap,
        flexWrap: wrap ? "wrap" : "nowrap",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Label({ children }) {
  return (
    <div style={{ fontSize: 11, color: T.faint, letterSpacing: ".06em", marginBottom: 5, textTransform: "uppercase" }}>
      {children}
    </div>
  );
}

export function TextInput({ style, ...rest }) {
  return (
    <input
      style={{
        width: "100%",
        boxSizing: "border-box",
        background: "rgba(0,0,0,0.35)",
        border: `1px solid ${T.line}`,
        borderRadius: 8,
        color: T.ink,
        fontFamily: T.font,
        fontSize: 13,
        padding: "8px 10px",
        outline: "none",
        ...style,
      }}
      {...rest}
    />
  );
}

export function Toggle({ label, checked, onChange }) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        fontSize: 13,
        color: checked ? T.ink : T.dim,
      }}
    >
      <span
        style={{
          width: 34,
          height: 19,
          borderRadius: 999,
          background: checked ? "rgba(255,16,38,0.5)" : "rgba(255,255,255,0.1)",
          border: `1px solid ${checked ? T.accent : T.line}`,
          position: "relative",
          transition: "background .15s",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 16 : 2,
            width: 13,
            height: 13,
            borderRadius: "50%",
            background: checked ? "#fff" : T.dim,
            transition: "left .15s",
          }}
        />
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ display: "none" }} />
      {label}
    </label>
  );
}
